
  import { defineComponent, ref, computed, onMounted } from 'vue';
  import { Field, Form, ErrorMessage } from 'vee-validate';
  import { useI18n } from 'vue-i18n';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import * as Yup from 'yup';
  import Swal from 'sweetalert2';
  import LookupNameIds from '@/store/enums/settingsEnums';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';

  export default defineComponent({
    name: 'package-discount-editing',
    components: { Field, Form, ErrorMessage },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const route = useRoute();
      const router = useRouter();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const translate = (text: string) => (te(text) ? t(text) : text);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('Update Package Discount'), [
          translate('settings'),
        ]);
      });

      const goBack = () => router.back();

      const { data } = await store.dispatch(
        Actions.GET_PACKAGE_DISCOUNT,
        route.params.id
      );
      const packageDiscount = ref({ ...data.data });

      await store.dispatch(Actions.GET_ALL_SETTINGS_LOOKUPS, [
        LookupNameIds.CLASS_TYPE,
      ]);

      await store.dispatch(Actions.GET_ALL_CLASS_CATEGORIES);

      const lookupsList = computed(() => store.getters.allLookupsList);

      const packageTypes = lookupsList.value.filter((lookup) => {
        return lookup.lookupNameId === LookupNameIds.CLASS_TYPE;
      });

      const classCategoryList = computed(
        () => store.getters.allClassCategoriesList
      );

      const validationSchema = Yup.object().shape({
        discountAmount: Yup.string().required(() =>
          translate('Discount Amount is required')
        ),
        numberOfLessons: Yup.string().required(() =>
          translate('Number of Lessons is required')
        ),

        packageTypeId: Yup.string().required(() =>
          translate('Package Type is required')
        ),

        classCategoryId: Yup.string().required(() =>
          translate('Class Category is required')
        ),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const formData = {
          ...values,
          discountAmount: String(values.discountAmount),
          numberOfLessons: String(values.numberOfLessons),
        };

        await store.dispatch(Actions.UPDATE_PACKAGE_DISCOUNT, {
          data: formData,
          id: packageDiscount.value.id,
        });

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
          });
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_PACKAGE_DISCOUNT'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
          }).then(() => {
            router.push({ name: 'package-discount-listing' });
          });
        }

        submitButton.value?.removeAttribute('data-kt-indicator');
        if (submitButton.value) submitButton.value.disabled = false;
      };

      return {
        packageDiscount,
        packageTypes,
        classCategoryList,
        submitButton,
        translate,
        goBack,
        validationSchema,
        can,
        onSubmitUpdate,
      };
    },
  });
